import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import "./Alert.css";

const FADE_DURATION = 300; // fade duration in ms

const NormalAlert = ({ type = "info", message, onClose, autoClose = 3000 }) => {
	const normalizedType =
		type.toLowerCase() === "fail" ? "error" : type.toLowerCase();
	const [visible, setVisible] = useState(false);
	const [progress, setProgress] = useState(100);

	useEffect(() => setVisible(true), []);

	useEffect(() => {
		if (!onClose || !autoClose) return;

		const start = Date.now();
		const interval = setInterval(() => {
			const elapsed = Date.now() - start;
			const remaining = Math.max(autoClose - elapsed, 0);
			setProgress((remaining / autoClose) * 100);
			if (remaining <= 0) clearInterval(interval);
		}, 50);

		const closeTimer = setTimeout(() => {
			setVisible(false);
			setTimeout(onClose, FADE_DURATION);
		}, autoClose);

		return () => {
			clearTimeout(closeTimer);
			clearInterval(interval);
		};
	}, [onClose, autoClose]);

	const handleClose = () => {
		setVisible(false);
		setTimeout(onClose, FADE_DURATION);
	};

	const types = {
		success: {
			color: "#22c55e",
			bg: "rgba(34,197,94,0.1)",
			icon: "bi-check-lg",
		},
		error: {
			color: "#ef4444",
			bg: "rgba(239,68,68,0.1)",
			icon: "bi-x-lg",
		},
		info: {
			color: "#0ea5e9",
			bg: "rgba(14,165,233,0.1)",
			icon: "bi-info-lg",
		},
		warning: {
			color: "#f59e0b",
			bg: "rgba(245,158,11,0.1)",
			icon: "bi-exclamation-lg",
		},
	};

	const style = types[normalizedType] || types.info;

	const modalContent = (
		<div style={styles.overlay}>
			<div
				style={{
					...styles.alertBox,
					opacity: visible ? 1 : 0,
					transition: `opacity ${FADE_DURATION}ms ease-in-out`,
				}}
			>
				{/* Progress bar (top border effect) */}
				<div style={styles.progressWrapper}>
					<div
						style={{
							...styles.progressBar,
							backgroundColor: style.color,
							width: `${progress}%`,
						}}
					/>
				</div>

				{/* Content */}
				<div style={styles.content}>
					<div
						style={{
							...styles.iconWrapper,
							backgroundColor: style.bg,
							color: style.color,
						}}
					>
						<i className={`bi ${style.icon}`}></i>
					</div>

					<div style={styles.textWrapper}>
						<div style={styles.message}>{message}</div>
					</div>

					<button onClick={handleClose} style={styles.closeButton}>
						<i className="bi bi-x"></i>
					</button>
				</div>
			</div>
		</div>
	);

	return ReactDOM.createPortal(modalContent, document.body);
};

const styles = {
	overlay: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.35)",
		display: "flex",
		justifyContent: "end",
		alignItems: "end",
		zIndex: 9999,
	},
	alertBox: {
		background: "linear-gradient(180deg, #ffffff 0%, #fdfdfd 100%)",
		borderRadius: 14,
		boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
		position: "relative",
		top: "-25px",
		right: "25px",
		width: "fit-content",
		minWidth: "300px",
		overflow: "hidden",
	},
	progressWrapper: {
		width: "100%",
		height: "4px",
		backgroundColor: "#e5e7eb",
		position: "absolute",
		top: 0,
		left: 0,
	},
	progressBar: {
		height: "100%",
		borderRadius: "0 0 2px 2px",
		transition: "width 100ms linear",
	},
	content: {
		display: "flex",
		alignItems: "center",
		gap: "14px",
		padding: "16px 22px",
	},
	iconWrapper: {
		width: "38px",
		height: "38px",
		borderRadius: "50%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		fontSize: "20px",
		flexShrink: 0,
	},
	textWrapper: {
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
	},
	message: {
		fontSize: "15px",
		color: "#222",
		lineHeight: "1.4",
		fontWeight: 500,
	},
	closeButton: {
		background: "transparent",
		border: "none",
		cursor: "pointer",
		color: "#777",
		fontSize: "18px",
		transition: "color 0.2s ease",
	},
};

export default NormalAlert;
